import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { CircleX } from "lucide-react";

import { cn } from "@/utils";
import { Spinner } from "@/components/ui/spinner";

export type InputPrimitiveProps = React.InputHTMLAttributes<HTMLInputElement>;

const InputPrimitive = React.forwardRef<HTMLInputElement, InputPrimitiveProps>(
  ({ className, type = "text", ...props }, ref) => {
    return (
      <input
        ref={ref}
        type={type}
        className={cn(
          "placeholder:text-neutral-400 text-neutral-900 flex h-10 w-full min-w-0 items-center rounded-lg border bg-white px-4 text-sm transition-colors outline-none file:inline-flex file:h-7 file:rounded-md file:border-0 file:bg-transparent file:px-2 file:text-sm file:font-medium hover:border-neutral-500 focus-visible:border-neutral-900 focus-visible:ring-0 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400 disabled:border-neutral-200 aria-invalid:border-destructive",
          className
        )}
        {...props}
      />
    );
  }
);

InputPrimitive.displayName = "InputPrimitive";

const inputVariants = cva("", {
  variants: {
    controlSize: {
      sm: "h-9 rounded-md px-3 text-sm",
      md: "h-10 rounded-lg px-4 text-sm",
      lg: "h-12 rounded-xl px-5 text-base"
    },
    fullWidth: {
      true: "w-full",
      false: "w-auto"
    },
    appearance: {
      filled: "border-neutral-200 hover:border-neutral-500 focus-visible:border-neutral-900",
      unfilled: "bg-white border-neutral-300 hover:border-neutral-500 focus-visible:border-neutral-900"
    }
  },
  defaultVariants: {
    controlSize: "md",
    fullWidth: true,
    appearance: "filled"
  }
});

export type InputProps = Omit<InputPrimitiveProps, "size"> &
  VariantProps<typeof inputVariants> & {
    prefix?: React.ReactNode;
    prefixProps?: React.HTMLAttributes<HTMLSpanElement>;
    prefixInteractive?: boolean;
    suffix?: React.ReactNode;
    suffixProps?: React.HTMLAttributes<HTMLSpanElement>;
    suffixInteractive?: boolean;
    wrapperClassName?: string;
    invalid?: boolean;
    loading?: boolean;
    loadingIcon?: React.ReactNode;
    validationMessage?: React.ReactNode;
    validationIcon?: React.ReactNode;
    validationMessageProps?: React.HTMLAttributes<HTMLParagraphElement>;
    onValueChange?: (value: string) => void;
  };

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      wrapperClassName,
      controlSize,
      fullWidth,
      appearance,
      prefix,
      prefixProps,
      prefixInteractive,
      suffix,
      suffixProps,
      suffixInteractive,
      invalid,
      loading,
      loadingIcon,
      validationMessage,
      validationIcon,
      validationMessageProps,
      onValueChange,
      type = "text",
      ...rest
    },
    ref
  ) => {
    const hasPrefix = Boolean(prefix);
    const hasSuffix = Boolean(suffix) || loading;
    const isFullWidth = fullWidth ?? true;
    const {
      "aria-invalid": ariaInvalidProp,
      "aria-describedby": ariaDescribedByProp,
      onChange: onChangeProp,
      ...inputProps
    } = rest;
    const ariaInvalid = invalid ?? ariaInvalidProp;
    const messageId = React.useId();
    const handleChange = React.useCallback<NonNullable<InputPrimitiveProps["onChange"]>>(
      (event) => {
        onChangeProp?.(event);
        onValueChange?.(event.target.value);
      },
      [onChangeProp, onValueChange]
    );

    const resolvedAriaInvalid =
      typeof ariaInvalid === "string" ? ariaInvalid
      : ariaInvalid ? true
      : undefined;

    const describedBy =
      validationMessage ?
        [ariaDescribedByProp, messageId].filter(Boolean).join(" ")
      : ariaDescribedByProp;

    const controlWrapperClassName = cn(
      "relative inline-flex items-center",
      isFullWidth ? "w-full" : "w-fit",
      !validationMessage && wrapperClassName
    );

    const inputElement = (
      <InputPrimitive
        ref={ref}
        type={type}
        data-slot="input"
        className={cn(
          inputVariants({ controlSize, fullWidth: isFullWidth, appearance }),
          hasPrefix && "pl-10",
          hasSuffix && "pr-10",
          className
        )}
        aria-invalid={resolvedAriaInvalid}
        aria-describedby={describedBy || undefined}
        onChange={handleChange}
        {...inputProps}
      />
    );

    if (!hasPrefix && !hasSuffix && !validationMessage) {
      return inputElement;
    }

    const { className: prefixClassName, ...prefixRest } = prefixProps ?? {};
    const { className: suffixClassName, ...suffixRest } = suffixProps ?? {};
    const { className: validationMessageClassName, ...validationMessageRest } =
      validationMessageProps ?? {};

    const suffixContent = (
      <>
        {suffix}
        {loading && (loadingIcon ?? <Spinner size={16} variant="muted" />)}
      </>
    );

    const inputWithAffixes = (
      <div data-slot="input-wrapper" className={controlWrapperClassName}>
        {hasPrefix && (
          <span
            {...prefixRest}
            className={cn(
              "absolute left-3 top-1/2 -translate-y-1/2 inline-flex items-center text-muted-foreground",
              !prefixInteractive && "pointer-events-none",
              prefixClassName
            )}
          >
            {prefix}
          </span>
        )}
        {inputElement}
        {hasSuffix && (
          <span
            {...suffixRest}
            className={cn(
              "absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-2 text-muted-foreground",
              !suffixInteractive && "pointer-events-none",
              suffixClassName
            )}
          >
            {suffixContent}
          </span>
        )}
      </div>
    );

    if (!validationMessage) {
      return inputWithAffixes;
    }

    return (
      <div className={cn("flex flex-col gap-1", isFullWidth ? "w-full" : "w-fit", wrapperClassName)}>
        {hasPrefix || hasSuffix ? inputWithAffixes : inputElement}
        <p
          id={messageId}
          {...validationMessageRest}
          className={cn("flex items-center gap-2 text-sm text-destructive", validationMessageClassName)}
        >
          {validationIcon ?? <CircleX className="size-4" />}
          <span>{validationMessage}</span>
        </p>
      </div>
    );
  }
);

Input.displayName = "Input";

export { InputPrimitive, Input, inputVariants };
